import {
  type FilterAdvancedType,
  type FilterBracketType,
  type FilterBudgetType,
  type FilterTimeframeType,
  isFilterBracketType,
  isFilterBudgetType,
  isFilterTimeframeType,
} from '@/defs/filters'
import { type CardtypeType, type ColorIdType, isCardtypeType, isColorIdType } from '@/defs/mtg'
import {
  TABS_CARD_FEATURED,
  TABS_COLOR_ID,
  TABS_COMMANDER_FEATURED,
  TABS_PRECON,
  TABS_SALT,
  TABS_TAGS,
  type TabType,
} from '@/defs/tab'
import { isTagCategoryType, type TagCategoryType } from '@/defs/tag'
import { isNonEmptyString } from '@/utils/commonUtil'

export const SUBSET_SLUGS = ['name', 'popularity'] as const
export type SubsetSlugType = (typeof SUBSET_SLUGS)[number]
export const isSubsetSlugType = (slug?: string) => SUBSET_SLUGS.includes(slug as SubsetSlugType)

export const ROUTE_SECTIONS = [
  'article',
  'articlesAuthor',
  'articlesCategory',
  'articlesLanding',
  'articlesPreview',
  'articlesSearch',
  'articlesTag',
  'banned',
  'cardsFeatured',
  'cardtypes',
  'combos',
  'combosEarlyGame',
  'combosFeatured',
  'combosLateGame',
  'commandersFeatured',
  'commandersList',
  'companions',
  'deckpreview',
  'gameChangers',
  'guide',
  'guidesLanding',
  'home',
  'landsList',
  'manaRocks',
  'partnersList',
  'partnersFeatured',
  'preconsFeatured',
  'preconsList',
  'recs',
  'salt',
  'sets',
  'setsList',
  'staples',
  'tags',
  'tagsList',
  'top',
  'vehicles',
] as const
export type RouteSectionType = (typeof ROUTE_SECTIONS)[number]

export type RouteParamsType = {
  advancedFilters?: FilterAdvancedType[]
  articleCategorySlug?: string
  articleSlug?: string
  articleTagSlug?: string
  authorSlug?: string
  bracket?: FilterBracketType
  budget?: FilterBudgetType
  cardSlug?: string
  cardtype?: CardtypeType
  colorId?: ColorIdType
  comboSlug?: string
  deckpreviewSlug?: string
  pageNumber?: number
  preconCommanderSlug?: string
  preconSlug?: string
  search?: string
  setSlug?: string
  subsetSlug?: SubsetSlugType
  tagCategorySlug?: TagCategoryType
  tagSlug?: string
  timeframe?: FilterTimeframeType
}

export type RouteType = RouteParamsType & {
  section: RouteSectionType
  tab?: string
}

export type RouteParamsConfigType = {
  check: (key?: string) => boolean
  isFilter?: true
  isStrict?: true
  parser?: 'advancedFilters' | 'pageNumber'
  query?: string
}

const isNonReservedSlug = (slug?: string) => isNonEmptyString(slug) && !isFilterBudgetType(slug) && !isColorIdType(slug)

export const CONFIG_ROUTE_PARAM = {
  advancedFilters: { check: isNonEmptyString, isFilter: true, parser: 'advancedFilters', query: 'f' },
  articleCategorySlug: { check: isNonEmptyString },
  articleSlug: { check: isNonEmptyString, query: 'p' },
  articleTagSlug: { check: isNonEmptyString },
  authorSlug: { check: isNonEmptyString },
  bracket: { check: isFilterBracketType, isFilter: true },
  budget: { check: isFilterBudgetType, isFilter: true },
  cardSlug: { check: isNonEmptyString },
  cardtype: { check: isCardtypeType },
  colorId: { check: isColorIdType, isFilter: true },
  comboSlug: { check: isNonEmptyString },
  deckpreviewSlug: { check: isNonEmptyString },
  pageNumber: {
    check: (key?: string) => /^[1-9]\d*$/.test(key ?? ''),
    isStrict: true,
    parser: 'pageNumber',
    query: 'page',
  },
  preconCommanderSlug: { check: isNonEmptyString, isFilter: true },
  preconSlug: { check: isNonEmptyString },
  search: { check: isNonEmptyString, query: 'q' },
  setSlug: { check: isNonEmptyString },
  subsetSlug: { check: isSubsetSlugType },
  tagCategorySlug: { check: isTagCategoryType },
  tagSlug: { check: isNonReservedSlug, isFilter: true },
  timeframe: { check: isFilterTimeframeType, isFilter: true },
} as const satisfies Record<keyof RouteParamsType, RouteParamsConfigType>

export type RouteQueryParamType = {
  [K in keyof typeof CONFIG_ROUTE_PARAM]: (typeof CONFIG_ROUTE_PARAM)[K] extends { query: string } ? K : never
}[keyof typeof CONFIG_ROUTE_PARAM]
export const isRouteParamType = (key?: string): key is keyof RouteParamsType => !!key && key in CONFIG_ROUTE_PARAM
export const getRouteParamConfig = (param: keyof RouteParamsType): RouteParamsConfigType => CONFIG_ROUTE_PARAM[param]

export type RouteParamValueType = RouteParamsType[keyof RouteParamsType]

export type RouteConfigType = {
  queryParams?: RouteQueryParamType[]
  tabs?: TabType[]
  template: string
}

export const CONFIG_ROUTE = {
  article: {
    template: 'articles/[articleSlug]',
  },
  articlesAuthor: {
    queryParams: ['pageNumber'],
    template: 'articles/author/[authorSlug]',
  },
  articlesCategory: {
    queryParams: ['pageNumber'],
    template: 'articles/category/[articleCategorySlug]',
  },
  articlesLanding: {
    queryParams: ['pageNumber'],
    template: 'articles',
  },
  articlesPreview: {
    queryParams: ['articleSlug'],
    template: 'articles/preview',
  },
  articlesSearch: {
    queryParams: ['search', 'pageNumber'],
    template: 'articles/search',
  },
  articlesTag: {
    queryParams: ['pageNumber'],
    template: 'articles/tag/[articleTagSlug]',
  },
  banned: {
    template: 'banned',
  },
  cardsFeatured: {
    tabs: TABS_CARD_FEATURED,
    template: 'cards/[cardSlug]/[[colorId]]',
  },
  cardtypes: {
    queryParams: ['pageNumber'],
    template: 'top/[[cardtype]]/[[timeframe]]',
  },
  combos: {
    tabs: TABS_COLOR_ID,
    template: 'combos/[[colorId]]',
  },
  combosEarlyGame: {
    template: 'combos/early-game-2-card-combos',
  },
  combosFeatured: {
    template: 'combos/[colorId]/[comboSlug]',
  },
  combosLateGame: {
    template: 'combos/late-game-2-card-combos',
  },
  commandersFeatured: {
    queryParams: ['advancedFilters'],
    tabs: TABS_COMMANDER_FEATURED,
    template: 'commanders/[cardSlug]/[[colorId]]/[[bracket]]/[[tagSlug]]/[[budget]]',
  },
  commandersList: {
    queryParams: ['pageNumber'],
    tabs: TABS_COLOR_ID,
    template: 'commanders/[[colorId]]/[[timeframe]]',
  },
  companions: {
    tabs: TABS_TAGS,
    template: 'companions',
  },
  deckpreview: {
    template: 'deckpreview/[deckpreviewSlug]',
  },
  gameChangers: {
    template: 'game-changers',
  },
  guide: {
    template: 'guides/[articleSlug]',
  },
  guidesLanding: {
    queryParams: ['pageNumber'],
    template: 'guides',
  },
  home: {
    template: '',
  },
  landsList: {
    tabs: TABS_COLOR_ID,
    template: 'lands/[[colorId]]/[[subsetSlug]]',
  },
  manaRocks: {
    queryParams: ['pageNumber'],
    tabs: TABS_COLOR_ID,
    template: 'mana-rocks/[[colorId]]',
  },
  partnersFeatured: {
    template: 'partners/[cardSlug]',
  },
  partnersList: {
    template: 'partners',
  },
  preconsFeatured: {
    tabs: TABS_PRECON,
    template: 'precons/[preconSlug]/[[preconCommanderSlug]]',
  },
  preconsList: {
    template: 'precons',
  },
  recs: {
    template: 'recs',
  },
  salt: {
    queryParams: ['pageNumber'],
    tabs: TABS_SALT,
    template: 'top/salt',
  },
  sets: {
    template: 'sets/[setSlug]',
  },
  setsList: {
    template: 'sets',
  },
  staples: {
    queryParams: ['pageNumber'],
    tabs: TABS_COLOR_ID,
    template: 'staples/[colorId]',
  },
  tags: {
    template: 'tags/[tagSlug]/[[colorId]]/[[budget]]',
  },
  tagsList: {
    tabs: TABS_TAGS,
    template: 'tags/[[tagCategorySlug]]/[[colorId]]',
  },
  top: {
    queryParams: ['pageNumber'],
    tabs: TABS_COLOR_ID,
    template: 'top/[[colorId]]/[[timeframe]]',
  },
  vehicles: {
    template: 'commanders/vehicles',
  },
} as const satisfies Record<RouteSectionType, RouteConfigType>

export const getRouteConfig = (section: RouteSectionType): RouteConfigType => CONFIG_ROUTE[section]
